import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Icon,
  IconButton,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../../shared/components';
import { feedback, finishRequest } from '../../../shared/services';
import { IBotTool, whatsAppService } from '../../../shared/services/api/whatsapp';
import { IPropsBusinessPage } from '../Business';

export const WhatsAppBotTools: React.FC<IPropsBusinessPage> = ({ business }) => {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['whatsapp-tools', business.id],
    queryFn: () => whatsAppService.getTools(business.id),
  });

  const tools = data?.tools ?? [];

  const { mutate: toggleTool } = useMutation(
    ({ toolId, enabled }: { toolId: number; enabled: boolean }) =>
      whatsAppService.updateTool(business.id, toolId, { enabled }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['whatsapp-tools', business.id]);
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const { mutate: deleteTool } = useMutation(
    (toolId: number) => whatsAppService.deleteTool(business.id, toolId),
    {
      onSuccess: () => {
        finishRequest('Ferramenta removida', 'success', () => {
          queryClient.invalidateQueries(['whatsapp-tools', business.id]);
          setConfirmDelete(null);
        });
      },
      onError: (error: Error) => {
        feedback(error.message, 'error');
        setConfirmDelete(null);
      },
    }
  );

  if (isFetching) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant='text' width={200} height={32} sx={{ mb: 1 }} />
          <Divider sx={{ mb: 2 }} />
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant='rectangular' height={48} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
          <Typography variant='h6'>Ferramentas do Bot</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {tools.length === 0 && !isFetching && (
          <Typography color='text.secondary' textAlign='center' py={4}>
            Nenhuma ferramenta encontrada. Ative o bot primeiro.
          </Typography>
        )}

        {tools.length > 0 && (
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align='center'>Ativa</TableCell>
                  <TableCell align='center'>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tools.map((tool: IBotTool) => (
                  <TableRow key={tool.id}>
                    <TableCell>
                      <Typography variant='body2' fontWeight='medium'>
                        {tool.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2' color='text.secondary'>
                        {tool.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tool.isSystem ? 'Sistema' : 'Custom'}
                        size='small'
                        color={tool.isSystem ? 'primary' : 'default'}
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell align='center'>
                      <Switch
                        checked={tool.enabled}
                        onChange={(_, checked) =>
                          toggleTool({ toolId: tool.id, enabled: checked })
                        }
                        size='small'
                      />
                    </TableCell>
                    <TableCell align='center'>
                      {!tool.isSystem && (
                        <>
                          {confirmDelete === tool.id ? (
                            <Box display='flex' gap={0.5} justifyContent='center'>
                              <Button
                                size='small'
                                color='error'
                                onClick={() => deleteTool(tool.id)}
                              >
                                Confirmar
                              </Button>
                              <Button
                                size='small'
                                variant='text'
                                onClick={() => setConfirmDelete(null)}
                              >
                                Cancelar
                              </Button>
                            </Box>
                          ) : (
                            <Tooltip title='Excluir ferramenta'>
                              <IconButton
                                size='small'
                                color='error'
                                onClick={() => setConfirmDelete(tool.id)}
                              >
                                <Icon>delete</Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};
